import { domToBlob } from "modern-screenshot";

/**
 * Capturing the current screen for the feedback dialog (#319).
 *
 * `modern-screenshot` draws the DOM into a canvas rather than asking for
 * `getDisplayMedia` — no permission prompt, and no picker asking someone to
 * choose which tab to share when the answer is always "this one".
 */

/**
 * `scale: 1` regardless of device pixel ratio. A capture at 3x on a phone
 * comfortably clears the 2 MB cap before a single blackout box is drawn, and
 * a bug report does not need retina detail to be useful.
 */
const CAPTURE_SCALE = 1;

function isSameOrigin(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * An `<img>` modern-screenshot cannot embed without tainting the canvas —
 * Shopify's CDN does not send the CORS headers a canvas read needs, so a
 * product thumbnail is the expected case here, not a bug.
 */
function isCrossOriginImage(node: Node): boolean {
  return node instanceof HTMLImageElement && !isSameOrigin(node.src);
}

/**
 * The current screen as a PNG blob, or `null` when capture is not possible
 * at all. Cross-origin images are the anticipated failure — the first
 * attempt includes everything, and only on a canvas security error does the
 * retry drop them, per the decision that a screenshot without product images
 * beats no screenshot.
 */
export async function captureScreenshot(): Promise<Blob | null> {
  const target = document.body;
  const base = { backgroundColor: "#ffffff", scale: CAPTURE_SCALE };

  try {
    return await domToBlob(target, base);
  } catch {
    try {
      return await domToBlob(target, {
        ...base,
        filter: (node) => !isCrossOriginImage(node),
      });
    } catch {
      return null;
    }
  }
}
